import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/apiClient";
import { BRAND } from "@/constants/brand";

export function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      if (isLogin) await login(email, password);
      else await register(name, email, password);
      toast.success(isLogin ? "Welcome back, tesoro!" : "Welcome to the family!");
      navigate(params.get("next") === "premium" ? "/?premium=1" : "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-cream px-4 py-10">
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center gap-2 text-sm text-espressolt hover:text-loretta-red" data-testid="back-to-chat">
          <ArrowLeft className="h-4 w-4" /> Back to the kitchen
        </Link>
        <div className="rounded-3xl border border-espresso/10 bg-white p-8 shadow-[0_8px_30px_rgba(44,30,22,0.08)]">
          <div className="mb-6 flex flex-col items-center text-center">
            <img src={BRAND.logo} alt="Aunt Lorettas Kitchen Chat" className="h-20 w-20 rounded-full object-contain" />
            <h1 className="mt-3 font-serif text-3xl font-bold text-espresso">{isLogin ? "Welcome back" : "Join the family"}</h1>
            <p className="mt-1 text-sm text-espressolt">{isLogin ? "Sign in to your kitchen" : "Create an account to save your kitchen"}</p>
          </div>

          {error && <p className="mb-4 rounded-xl bg-loretta-red/10 px-4 py-2.5 text-sm text-loretta-red" data-testid="auth-error">{error}</p>}

          <form onSubmit={submit} className="space-y-4">
            {!isLogin && (
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-espresso/50">Name</label>
                <input value={name} onChange={(e) => setName(e.target.value)} required data-testid="name-input" className="w-full rounded-xl border border-espresso/15 bg-cream px-4 py-3 text-sm outline-none focus:border-loretta-red" />
              </div>
            )}
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-espresso/50">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="email-input" className="w-full rounded-xl border border-espresso/15 bg-cream px-4 py-3 text-sm outline-none focus:border-loretta-red" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-espresso/50">Password</label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={6} data-testid="password-input" className="w-full rounded-xl border border-espresso/15 bg-cream px-4 py-3 text-sm outline-none focus:border-loretta-red" />
            </div>
            <button type="submit" disabled={loading} data-testid="auth-submit-button" className="w-full rounded-full bg-loretta-red py-3.5 font-semibold text-white transition-colors hover:bg-loretta-redhover disabled:opacity-60">
              {loading ? "One moment…" : isLogin ? "Sign in" : "Create account"}
            </button>
          </form>

          <p className="mt-5 text-center text-sm text-espressolt">
            {isLogin ? "New here? " : "Already have an account? "}
            <Link to={isLogin ? "/register" : "/login"} className="font-semibold text-loretta-red hover:underline" data-testid="auth-switch-link">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
